'use client'

import { LuMinus, LuPlus } from 'react-icons/lu'

import { cn } from '@/core/utils/common'
import { toFixedNumber } from '@/core/utils/percentage'

type NumberStepperProps = {
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
  step?: number
  label?: string
  error?: string
  disabled?: boolean
  className?: string
}

export default function NumberStepper({
  value,
  onChange,
  min = 0,
  max = Infinity,
  step = 1,
  label,
  error,
  disabled = false,
  className,
}: NumberStepperProps) {
  const precision = Math.max(getDecimalPlaces(value), getDecimalPlaces(step))
  const decreaseValue = toFixedNumber(value - step, precision)
  const increaseValue = toFixedNumber(value + step, precision)
  const validStep = Number.isFinite(step) && step > 0 && Number.isFinite(value)
  const canDecrease =
    validStep &&
    Number.isFinite(decreaseValue) &&
    decreaseValue < value &&
    decreaseValue >= min
  const canIncrease =
    validStep &&
    Number.isFinite(increaseValue) &&
    increaseValue > value &&
    increaseValue <= max

  const decrease = () => {
    if (canDecrease) onChange(decreaseValue)
  }

  const increase = () => {
    if (canIncrease) onChange(increaseValue)
  }

  return (
    <div className={cn(className)}>
      {label && (
        <p className="mb-2 block text-c14 font-medium text-foreground">
          {label}
        </p>
      )}
      <div
        className={cn(
          'inline-flex items-center rounded-field border border-border bg-surface-raised p-1',
          'transition-colors focus-within:border-point',
          disabled && 'pointer-events-none opacity-40'
        )}
      >
        <button
          type="button"
          onClick={decrease}
          disabled={!canDecrease || disabled}
          data-focus-appearance="fill"
          className={cn(
            'flex size-9 items-center justify-center rounded-control transition-colors',
            'focus:outline-none focus-visible:bg-surface-strong focus-visible:text-point',
            canDecrease && !disabled
              ? 'cursor-pointer text-foreground hover:bg-point/10 hover:text-point'
              : 'cursor-not-allowed text-description opacity-40'
          )}
          aria-label="감소"
        >
          <LuMinus aria-hidden="true" className="size-4" />
        </button>
        <span
          aria-live="polite"
          className="min-w-14 select-none px-3 text-center font-mono text-c15 font-bold tabular-nums text-point"
        >
          {value}
        </span>
        <button
          type="button"
          onClick={increase}
          disabled={!canIncrease || disabled}
          data-focus-appearance="fill"
          className={cn(
            'flex size-9 items-center justify-center rounded-control transition-colors',
            'focus:outline-none focus-visible:bg-surface-strong focus-visible:text-point',
            canIncrease && !disabled
              ? 'cursor-pointer text-foreground hover:bg-point/10 hover:text-point'
              : 'cursor-not-allowed text-description opacity-40'
          )}
          aria-label="증가"
        >
          <LuPlus aria-hidden="true" className="size-4" />
        </button>
      </div>
      {error && (
        <p role="alert" className="mt-2 text-c12 leading-5 text-error">
          {error}
        </p>
      )}
    </div>
  )
}

function getDecimalPlaces(value: number): number {
  const [coefficient, exponent = '0'] = String(value).split('e')
  return Math.max(
    0,
    (coefficient.split('.')[1]?.length ?? 0) - Number(exponent)
  )
}
